import apiClient from "./apiClient";

export const MASTER_DOMAIN_OPTIONS = [
  { label: "Case Property Type", value: "case_property_type" },
  { label: "Unit", value: "unit" },
  { label: "Act", value: "act" },
  { label: "Case Property Status", value: "case_property_status" },
  { label: "Vehicle Reason", value: "vehicle_reason" },
];

const SUPPORTED_LANGUAGES = ["en", "gu", "hi", "mr"];

const normalizeText = (value) => String(value ?? "").trim();
const normalizeLowerText = (value) => normalizeText(value).toLowerCase();
const normalizeOptionalState = (value) => {
  const normalizedValue = normalizeText(value);
  const loweredValue = normalizeLowerText(normalizedValue);

  if (!normalizedValue || loweredValue === "null" || loweredValue === "undefined") {
    return "";
  }

  return normalizedValue;
};

const ensureDomain = (domain) => {
  const matchedDomain = MASTER_DOMAIN_OPTIONS.find(
    (option) => option.value === domain
  );

  if (!matchedDomain) {
    throw new Error("Invalid master domain selected.");
  }

  return matchedDomain.value;
};

const normalizeLang = (lang) => {
  const normalizedLang = normalizeLowerText(lang);
  return SUPPORTED_LANGUAGES.includes(normalizedLang) ? normalizedLang : "en";
};

const normalizeStates = (states = []) =>
  Array.from(
    new Set(
      (Array.isArray(states) ? states : [])
        .map((state) => normalizeText(state))
        .filter(Boolean)
    )
  );

const normalizeAliases = (aliases = []) =>
  Array.from(
    new Set(
      (Array.isArray(aliases) ? aliases : [])
        .map((alias) => normalizeText(alias))
        .filter(Boolean)
    )
  );

const normalizeLabels = (labels = {}, { requireEnglish = false } = {}) => {
  const nextLabels = Object.entries({
    en: normalizeText(labels.en),
    gu: normalizeText(labels.gu),
    hi: normalizeText(labels.hi),
    mr: normalizeText(labels.mr),
  }).reduce((accumulator, [lang, label]) => {
    if (label) {
      accumulator[lang] = label;
    }

    return accumulator;
  }, {});

  if (requireEnglish && !nextLabels.en) {
    throw new Error("English label is required.");
  }

  return nextLabels;
};

const normalizeSortOrder = (value) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    throw new Error("Sort order must be a valid number.");
  }

  return parsedValue;
};

const sortItems = (items = []) =>
  [...items].sort((left, right) => {
    if (left.sort_order !== right.sort_order) {
      return left.sort_order - right.sort_order;
    }

    return String(left.label ?? "").localeCompare(String(right.label ?? ""));
  });

const normalizeMasterItem = (item = {}) => {
  const states = normalizeStates(item.states);

  return {
    code: normalizeText(item.code),
    id: normalizeText(item.id),
    is_active: item.is_active !== false,
    is_global:
      typeof item.is_global === "boolean" ? item.is_global : states.length === 0,
    label: normalizeText(item.label),
    label_lang: normalizeText(item.label_lang),
    sort_order: normalizeSortOrder(item.sort_order ?? 0),
    states,
  };
};

const buildParams = ({
  domains = [],
  includeInactive = false,
  lang = "en",
  state = "",
} = {}) => {
  const params = {};

  if (Array.isArray(domains) && domains.length > 0) {
    params.domains = domains.map(ensureDomain).join(",");
  }

  if (lang) {
    params.lang = normalizeLang(lang);
  }

  const normalizedState = normalizeOptionalState(state);

  if (normalizedState) {
    params.state = normalizedState;
  }

  if (includeInactive) {
    params.include_inactive = 1;
  }

  return params;
};

const getErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallbackMessage;

const sanitizePayload = (payload = {}, mode = "create") => {
  const nextPayload = {};

  if (mode === "create") {
    const code = normalizeText(payload.code);

    if (!code) {
      throw new Error("Code is required.");
    }

    if (!/^[A-Z0-9_]+$/.test(code)) {
      throw new Error("Code must contain only A-Z, 0-9, and underscore.");
    }

    nextPayload.code = code;
  }

  if (payload.labels !== undefined) {
    const labels = normalizeLabels(payload.labels, {
      requireEnglish: mode === "create",
    });

    if (Object.keys(labels).length > 0) {
      nextPayload.labels = labels;
    }
  }

  if (payload.sort_order !== undefined) {
    nextPayload.sort_order = normalizeSortOrder(payload.sort_order);
  }

  if (payload.states !== undefined) {
    nextPayload.states = normalizeStates(payload.states);
  }

  if (payload.aliases !== undefined) {
    nextPayload.aliases = normalizeAliases(payload.aliases);
  }

  if (payload.is_active !== undefined) {
    nextPayload.is_active = Boolean(payload.is_active);
  }

  return nextPayload;
};

export const getMastersCatalog = async ({
  domains = [],
  lang = "en",
  state = "",
} = {}) => {
  try {
    const requestedDomains =
      Array.isArray(domains) && domains.length > 0
        ? domains.map(ensureDomain)
        : MASTER_DOMAIN_OPTIONS.map((option) => option.value);
    const response = await apiClient.get("/api/masters", {
      params: buildParams({
        domains: requestedDomains,
        lang,
        state,
      }),
    });
    const responseData = response.data || {};
    const masters = requestedDomains.reduce((accumulator, domain) => {
      const domainItems = Array.isArray(responseData?.masters?.[domain])
        ? responseData.masters[domain]
        : [];

      accumulator[domain] = sortItems(domainItems.map(normalizeMasterItem));
      return accumulator;
    }, {});

    return {
      lang: normalizeLang(responseData.lang || lang),
      masters,
      state:
        responseData.state === null || responseData.state === undefined
          ? normalizeOptionalState(state) || null
          : responseData.state,
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, "Failed to load master data."));
  }
};

export const getMasterDomainItems = async (
  domain,
  { includeInactive = false, lang = "en", state = "" } = {}
) => {
  const normalizedDomain = ensureDomain(domain);

  try {
    const response = await apiClient.get(`/api/masters/${normalizedDomain}`, {
      params: buildParams({
        includeInactive,
        lang,
        state,
      }),
    });
    const responseData = response.data || {};
    const rawItems = Array.isArray(responseData.items)
      ? responseData.items
      : Array.isArray(responseData?.masters?.[normalizedDomain])
      ? responseData.masters[normalizedDomain]
      : [];

    return {
      domain: responseData.domain || normalizedDomain,
      items: sortItems(rawItems.map(normalizeMasterItem)),
      lang: normalizeLang(responseData.lang || lang),
      state:
        responseData.state === null || responseData.state === undefined
          ? normalizeOptionalState(state) || null
          : responseData.state,
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, "Failed to load master data."));
  }
};

export const getMasterRecordTranslations = async (
  domain,
  masterId,
  { includeInactive = true, state = "" } = {}
) => {
  const normalizedDomain = ensureDomain(domain);
  const normalizedMasterId = normalizeText(masterId);

  try {
    const responses = await Promise.all(
      SUPPORTED_LANGUAGES.map(async (lang) => ({
        requestedLang: lang,
        response: await getMasterDomainItems(normalizedDomain, {
          includeInactive,
          lang,
          state,
        }),
      }))
    );

    const matchedRecords = responses
      .map((entry) => ({
        lang: entry.requestedLang,
        record: entry.response.items.find((item) => item.id === normalizedMasterId),
      }))
      .filter((entry) => entry.record);

    if (matchedRecords.length === 0) {
      throw new Error("Master record not found.");
    }

    const baseRecord = matchedRecords[0].record;
    const labels = matchedRecords.reduce((accumulator, entry) => {
      const requestedLang = normalizeLang(entry.lang);
      const rawLabelLang = normalizeText(entry.record?.label_lang);
      const recordLabelLang = rawLabelLang
        ? normalizeLang(rawLabelLang)
        : requestedLang;

      if (requestedLang === "en" || recordLabelLang === requestedLang) {
        accumulator[requestedLang] = entry.record.label;
      }

      return accumulator;
    }, {});

    return {
      ...baseRecord,
      labels,
    };
  } catch (error) {
    throw new Error(
      getErrorMessage(error, "Failed to load master value details.")
    );
  }
};

export const createMasterRecord = async (domain, payload) => {
  const normalizedDomain = ensureDomain(domain);

  try {
    const response = await apiClient.post(
      `/api/masters/${normalizedDomain}`,
      sanitizePayload(payload, "create")
    );

    return {
      data: response.data,
      message:
        response.data?.message || "Master value created successfully.",
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, "Failed to create master value."));
  }
};

export const updateMasterRecord = async (domain, masterId, payload) => {
  const normalizedDomain = ensureDomain(domain);

  try {
    const response = await apiClient.put(
      `/api/masters/${normalizedDomain}/${masterId}`,
      sanitizePayload(payload, "update")
    );

    return {
      data: response.data,
      message:
        response.data?.message || "Master value updated successfully.",
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, "Failed to update master value."));
  }
};

export const deactivateMasterRecord = async (domain, masterId) => {
  const normalizedDomain = ensureDomain(domain);

  try {
    const response = await apiClient.delete(
      `/api/masters/${normalizedDomain}/${masterId}`
    );

    return {
      data: response.data,
      message:
        response.data?.message || "Master value deactivated successfully.",
    };
  } catch (error) {
    throw new Error(
      getErrorMessage(error, "Failed to deactivate master value.")
    );
  }
};
